import PageLayout from "@/components/PageLayout";
import { Target, Eye, Trophy, Globe, MessageSquare, Lightbulb, Users, Rocket, CheckCircle } from "lucide-react";
import ceoImg from "@/assets/ceo.png";
import aqibImg from "@/assets/aqib.png";

const goals = [
  "Deliver premium digital solutions for global brands",
  "Enhance brand visibility and audience engagement",
  "Provide result-driven strategies for sustainable growth",
];

const stats = [
  { value: "05+", label: "Active Clients" },
  { value: "7+", label: "Projects in Progress" },
  { value: "73+", label: "Satisfied Clients" },
  { value: "300+", label: "Completed Orders" },
  { value: "80+", label: "Brands Joined" },
  { value: "3+", label: "Years of Experience" },
];

const processSteps = [
  { icon: MessageSquare, title: "Start Consultation", desc: "Personalized digital marketing strategies tailored for growth." },
  { icon: Lightbulb, title: "Book A Service", desc: "Choose the right service for your brand and get started." },
  { icon: Users, title: "Get Results", desc: "Watch your brand grow with measurable outcomes and ROI." },
];

const AboutPage = () => {
  return (
    <PageLayout title="About Us" subtitle="Welcome To Graphic Lines – Professional Digital Agency">
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          
          {/* Main About Section */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="relative flex justify-center">
              <div className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full gradient-hero mx-auto relative overflow-hidden">
                <img src={aqibImg} alt="Graphic Lines founder" className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 sm:w-60 md:w-72 lg:w-80 z-10 object-contain" />
              </div>
            </div>

            <div className="text-center lg:text-left">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-widest uppercase mb-4">About Us</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-4">About Graphic Lines</h2>
              <p className="text-muted-foreground leading-relaxed mb-4 text-sm md:text-base">
                Graphic Lines is a professional digital agency committed to helping businesses grow, scale, and shine in the digital world. Founded 3 years ago, we specialize in graphic designing, video editing, digital marketing, branding, ads management, and social media management.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6 text-sm md:text-base">
                Our mission is to turn ideas into impactful visuals and strategies that connect with audiences worldwide.
              </p>

              <h3 className="text-base md:text-lg font-bold text-foreground mb-3">Our Goals</h3>
              <ul className="space-y-2 mb-6 text-left max-w-md mx-auto lg:mx-0">
                {goals.map((g) => (
                  <li key={g} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Trophy className="w-4 h-4 text-primary flex-shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Mission, Vision, Goal Cards */}
          <div className="mt-14 md:mt-20">
            <div className="text-center mb-8 md:mb-12">
              <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">Our Mission, Vision & Goal</span>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
              <div className="text-center bg-card border border-border rounded-2xl p-6 md:p-8 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full gradient-hero mx-auto flex items-center justify-center mb-4 md:mb-5">
                  <Target className="w-6 h-6 md:w-7 md:h-7 text-primary-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-primary mb-3">Our Mission</h3>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  To empower businesses with creative, strategic, and innovative digital services that drive measurable results and long-term success.
                </p>
              </div>
              <div className="text-center gradient-hero rounded-2xl p-6 md:p-8 shadow-xl">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-4 md:mb-5">
                  <Eye className="w-6 h-6 md:w-7 md:h-7 text-primary-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-primary-foreground mb-3">Our Vision</h3>
                <p className="text-xs md:text-sm text-primary-foreground/80 leading-relaxed">
                  To become a leading global digital agency, recognized for excellence, creativity, and impactful solutions that help brands thrive online.
                </p>
              </div>
              <div className="text-center bg-card border border-border rounded-2xl p-6 md:p-8 hover:shadow-xl transition-shadow sm:col-span-2 md:col-span-1">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full gradient-hero mx-auto flex items-center justify-center mb-4 md:mb-5">
                  <Globe className="w-6 h-6 md:w-7 md:h-7 text-primary-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-primary mb-3">Our Goal</h3>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  To deliver premium digital solutions for global brands, enhance brand visibility, and provide result-driven strategies for sustainable growth worldwide.
                </p>
              </div>
            </div>
          </div>

          {/* NEW: Meet the CEO & Founder Section */}
          <div className="mt-20 md:mt-28 bg-surface-alt border border-border rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              
              {/* Founder Image Column */}
              <div className="lg:col-span-5 relative mx-auto w-full max-w-md">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-card border-4 border-background shadow-2xl relative">
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent z-0"></div>
                  <img 
                    src={ceoImg} 
                    alt="CEO & Founder" 
                    className="w-full h-full object-cover object-top relative z-10" 
                  />
                </div>
              </div>

              {/* Founder Text Content */}
              <div className="lg:col-span-7">
                <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">
                  Leadership
                </span>
                
                {/* EXTRA BOLD CEO TITLE */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-6 tracking-tight">
                  Meet the <span className="text-primary">CEO & Founder</span>
                </h2>
                
                <div className="space-y-4 text-muted-foreground text-sm md:text-base leading-relaxed mb-8">
                  {/* BOLD DESCRIPTION INTRO */}
                  <p className="text-lg md:text-xl font-bold text-foreground leading-snug">
                    A creative-driven digital brand focused on delivering impactful design and marketing solutions.
                  </p>
                  <p>
                    A multi-skilled professional with expertise in <strong className="text-primary font-semibold">Graphic Design, Video Editing, Social Media Management, and Digital Marketing</strong>, he is dedicated to helping businesses grow, stand out, and convert audiences into customers.
                  </p>
                  <p>
                    Alongside leading Graphic Lines, he also manages a growing E-commerce brand, gaining hands-on experience in building, scaling, and optimizing online businesses.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h4 className="flex items-center gap-2 text-lg font-bold text-primary mb-2">
                        <Target className="w-5 h-5" /> Mission
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        To empower brands with creative visuals and smart digital strategies that not only look great but also drive real results.
                      </p>
                    </div>
                    <div>
                      <h4 className="flex items-center gap-2 text-lg font-bold text-primary mb-2">
                        <Rocket className="w-5 h-5" /> Vision
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        To become a leading digital brand that transforms ideas into powerful visual experiences and successful online businesses worldwide.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="flex items-center gap-2 text-lg font-bold text-primary mb-3">
                      <Trophy className="w-5 h-5" /> Core Goals
                    </h4>
                    <ul className="space-y-3">
                      {[
                        "Deliver high-quality and result-driven design solutions",
                        "Help businesses build a strong digital presence",
                        "Create content that engages, converts, and grows brands",
                        "Continuously innovate in design and marketing strategies."
                      ].map((goal, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                          <span className="leading-snug">{goal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Our Process */}
          <div className="mt-20 md:mt-28">
            <div className="text-center mb-8 md:mb-12">
              <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">Our Process</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-4">Easy Steps To Get Your Solution</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
                Graphic Lines makes digital marketing simple and effective.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
              {processSteps.map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
                    <div className="w-11 h-11 md:w-14 md:h-14 rounded-full gradient-hero flex items-center justify-center">
                      <step.icon className="w-5 h-5 md:w-6 md:h-6 text-primary-foreground" />
                    </div>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-primary mb-2">{step.title}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="mt-20 md:mt-28 text-center">
            <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">Our Skills</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-3">Our Skills & Experience</h2>
            <p className="text-secondary font-semibold mb-4 text-sm md:text-base">We Have Skilled and Experienced Digital Marketing Professionals.</p>
            <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed text-sm md:text-base">
              Our team at Graphic Lines has several years of experience in providing digital marketing services to help businesses create a powerful online presence.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6 mt-10 md:mt-16">
            {stats.map((s) => (
              <div key={s.label} className="text-center bg-card border border-border rounded-xl md:rounded-2xl p-4 md:p-6">
                <p className="text-2xl md:text-3xl font-display font-extrabold text-primary">{s.value}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>

        </div>
      </section>
    </PageLayout>
  );
};

export default AboutPage;
